import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { logger } from '../../../shared/lib/logger';
import { authFetch } from '../../../shared/api/authFetch';
import { config } from 'shared/config/config';

const BASE_URL = config.API_URL;

async function parseJsonOrThrow(response) {
  const text = await response.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch (e) {
    // ignore
  }
  if (!response.ok) {
    const msg = (data && (data.detail || data.message)) || text || `Request failed (${response.status})`;
    throw new Error(msg);
  }
  return data;
}

export const useSecureConnectorsQuery = (orgId) => {
  return useQuery({
    queryKey: ['secureConnectors', orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const resp = await authFetch(`${BASE_URL}/api/secure-connectors?orgId=${encodeURIComponent(orgId)}`, {
        method: 'GET',
      });
      const data = await parseJsonOrThrow(resp);
      return data?.connectors || data || [];
    },
    staleTime: 30 * 1000,
    refetchInterval: 5 * 1000,
  });
};

export const useSecureConnectorTemplatesQuery = (orgId) => {
  return useQuery({
    queryKey: ['secureConnectorTemplates', orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const resp = await authFetch(
        `${BASE_URL}/api/secure-connectors/templates?orgId=${encodeURIComponent(orgId)}`,
        { method: 'GET' }
      );
      const data = await parseJsonOrThrow(resp);
      return data?.templates || [];
    },
    staleTime: 5 * 60 * 1000,
  });
};

export const useSecureConnectorDefinitionsQuery = (orgId, connectorId) => {
  return useQuery({
    queryKey: ['secureConnectorDefinitions', orgId, connectorId],
    enabled: !!orgId && !!connectorId,
    queryFn: async () => {
      const resp = await authFetch(
        `${BASE_URL}/api/secure-connectors/${encodeURIComponent(connectorId)}/definitions?orgId=${encodeURIComponent(orgId)}`,
        { method: 'GET' }
      );
      return parseJsonOrThrow(resp);
    },
    staleTime: 15 * 1000,
  });
};

export const usePublishSecureConnectorDefinition = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ orgId, connectorId, versionId }) => {
      const resp = await authFetch(
        `${BASE_URL}/api/secure-connectors/${encodeURIComponent(connectorId)}/definitions/${encodeURIComponent(versionId)}/publish?orgId=${encodeURIComponent(orgId)}`,
        { method: 'POST' }
      );
      return parseJsonOrThrow(resp);
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({
        queryKey: ['secureConnectorDefinitions', vars.orgId, vars.connectorId],
      });
      qc.invalidateQueries({ queryKey: ['secureConnectors', vars.orgId] });
      toast.success('Connector configuration published; local approval is now required');
    },
    onError: (err) => {
      logger.error('Publish secure connector definition failed', { err });
      toast.error(err.message || 'Failed to publish connector configuration');
    },
  });
};

export const useCreateSecureConnector = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ orgId, name, templateKey }) => {
      const resp = await authFetch(`${BASE_URL}/api/secure-connectors?orgId=${encodeURIComponent(orgId)}`, {
        method: 'POST',
        body: JSON.stringify({ name, template_key: templateKey }),
      });
      return await parseJsonOrThrow(resp);
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['secureConnectors', vars.orgId] });
      toast.success('Secure Connector created');
    },
    onError: (err) => {
      logger.error('Create secure connector failed', { err });
      toast.error(err.message || 'Failed to create Secure Connector');
    },
  });
};

export const useDeleteSecureConnector = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ orgId, connectorId }) => {
      const resp = await authFetch(
        `${BASE_URL}/api/secure-connectors/${encodeURIComponent(connectorId)}?orgId=${encodeURIComponent(orgId)}`,
        { method: 'DELETE' }
      );
      if (!resp.ok) {
        await parseJsonOrThrow(resp);
      }
      return true;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['secureConnectors', vars.orgId] });
      // Deleting a connector cascades its secure-connector connections.
      qc.invalidateQueries({ queryKey: ['integrations', vars.orgId] });

      toast.success('Secure Connector deleted');
    },
    onError: (err) => {
      logger.error('Delete secure connector failed', { err });
      toast.error(err.message || 'Failed to delete Secure Connector');
    },
  });
};

export const useCreateItLink = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ orgId, connectorId }) => {
      const resp = await authFetch(
        `${BASE_URL}/api/secure-connectors/${encodeURIComponent(connectorId)}/it-link?orgId=${encodeURIComponent(orgId)}`,
        { method: 'POST' }
      );
      return parseJsonOrThrow(resp);
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['secureConnectors', vars.orgId] });
    },
    onError: (err) => {
      toast.error(err.message || 'The link could not be created');
    },
  });
};

export const useCancelItLink = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ orgId, connectorId }) => {
      const resp = await authFetch(
        `${BASE_URL}/api/secure-connectors/${encodeURIComponent(connectorId)}/it-link?orgId=${encodeURIComponent(orgId)}`,
        { method: 'DELETE' }
      );
      return parseJsonOrThrow(resp);
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['secureConnectors', vars.orgId] });
      toast.success('Link cancelled. It no longer works.');
    },
    onError: (err) => {
      toast.error(err.message || 'The link could not be cancelled');
    },
  });
};

export const useCreateEnrollmentToken = () => {
  return useMutation({
    mutationFn: async ({ orgId, connectorId, ttlMinutes = 60 }) => {
      const resp = await authFetch(
        `${BASE_URL}/api/secure-connectors/${encodeURIComponent(connectorId)}/enrollments?orgId=${encodeURIComponent(orgId)}&ttlMinutes=${encodeURIComponent(ttlMinutes)}`,
        { method: 'POST' }
      );
      return await parseJsonOrThrow(resp);
    },
    onError: (err) => {
      logger.error('Create enrollment token failed', { err });
      toast.error(err.message || 'Failed to create enrollment token');
    },
  });
};

export const useIssueAgentToken = () => {
  return useMutation({
    mutationFn: async ({ orgId, connectorId }) => {
      const resp = await authFetch(
        `${BASE_URL}/api/secure-connectors/${encodeURIComponent(connectorId)}/agent-tokens?orgId=${encodeURIComponent(orgId)}`,
        { method: 'POST' }
      );
      return await parseJsonOrThrow(resp);
    },
    onError: (err) => {
      logger.error('Issue agent token failed', { err });
      toast.error(err.message || 'Failed to issue agent token');
    },
  });
};

export const useListAgentTokens = (orgId, connectorId) => {
  return useQuery({
    queryKey: ['secureConnectorAgentTokens', orgId, connectorId],
    enabled: !!orgId && !!connectorId,
    queryFn: async () => {
      const resp = await authFetch(
        `${BASE_URL}/api/secure-connectors/${encodeURIComponent(connectorId)}/agent-tokens?orgId=${encodeURIComponent(orgId)}`,
        { method: 'GET' }
      );
      const data = await parseJsonOrThrow(resp);
      return data?.tokens || [];
    },
    staleTime: 30 * 1000,
  });
};

export const useRevokeAgentToken = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ orgId, connectorId, tokenId }) => {
      const resp = await authFetch(
        `${BASE_URL}/api/secure-connectors/${encodeURIComponent(connectorId)}/agent-tokens/${encodeURIComponent(tokenId)}/revoke?orgId=${encodeURIComponent(orgId)}`,
        { method: 'POST' }
      );
      return await parseJsonOrThrow(resp);
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['secureConnectorAgentTokens', vars.orgId, vars.connectorId] });
      toast.success('Agent token revoked');
    },
    onError: (err) => {
      logger.error('Revoke agent token failed', { err });
      toast.error(err.message || 'Failed to revoke agent token');
    },
  });
};

function getFilenameFromContentDisposition(headerValue) {
  if (!headerValue) return null;
  // e.g. attachment; filename="secure-connector-helm-bundle-<id>.tgz"
  const m = headerValue.match(/filename=\"?([^\";]+)\"?/i);
  return m?.[1] || null;
}

export async function downloadResponse(response, fallbackFilename) {
  if (!response.ok) {
    await parseJsonOrThrow(response);
  }
  const blob = await response.blob();
  const filename =
    getFilenameFromContentDisposition(response.headers.get('content-disposition'))
    || fallbackFilename;
  const url = window.URL.createObjectURL(blob);
  try {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    window.URL.revokeObjectURL(url);
  }
}

export async function downloadWindowsGuidedBundle({ orgId, connectorId }) {
  const resp = await authFetch(
    `${BASE_URL}/api/secure-connectors/${encodeURIComponent(connectorId)}/install/windows-guided-bundle?orgId=${encodeURIComponent(orgId)}`,
    { method: 'POST' }
  );
  return downloadResponse(resp, `LineSide-Secure-Connector-${connectorId}.zip`);
}

export async function downloadHelmBundle({ orgId, connectorId }) {
  const resp = await authFetch(
    `${BASE_URL}/api/secure-connectors/${encodeURIComponent(connectorId)}/install/helm-bundle?orgId=${encodeURIComponent(orgId)}`,
    { method: 'GET' }
  );
  if (!resp.ok) {
    await parseJsonOrThrow(resp);
  }
  const blob = await resp.blob();
  const filename = getFilenameFromContentDisposition(resp.headers.get('content-disposition')) || `secure-connector-${connectorId}.tgz`;

  const url = window.URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    window.URL.revokeObjectURL(url);
  }
}

export async function downloadDockerBundle({ orgId, connectorId }) {
  const resp = await authFetch(
    `${BASE_URL}/api/secure-connectors/${encodeURIComponent(connectorId)}/install/docker-bundle?orgId=${encodeURIComponent(orgId)}`,
    { method: 'GET' }
  );
  if (!resp.ok) {
    await parseJsonOrThrow(resp);
  }
  const blob = await resp.blob();
  const filename = getFilenameFromContentDisposition(resp.headers.get('content-disposition')) || `secure-connector-docker-${connectorId}.tgz`;

  const url = window.URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    window.URL.revokeObjectURL(url);
  }
}

export async function downloadLinuxSystemdBundle({ orgId, connectorId }) {
  const resp = await authFetch(
    `${BASE_URL}/api/secure-connectors/${encodeURIComponent(connectorId)}/install/linux-systemd-bundle?orgId=${encodeURIComponent(orgId)}`,
    { method: 'GET' }
  );
  if (!resp.ok) {
    await parseJsonOrThrow(resp);
  }
  const blob = await resp.blob();
  const filename = getFilenameFromContentDisposition(resp.headers.get('content-disposition')) || `secure-connector-linux-systemd-${connectorId}.tgz`;

  const url = window.URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    window.URL.revokeObjectURL(url);
  }
}

export async function downloadWindowsNssmBundle({ orgId, connectorId }) {
  const resp = await authFetch(
    `${BASE_URL}/api/secure-connectors/${encodeURIComponent(connectorId)}/install/windows-nssm-bundle?orgId=${encodeURIComponent(orgId)}`,
    { method: 'GET' }
  );
  if (!resp.ok) {
    await parseJsonOrThrow(resp);
  }
  const blob = await resp.blob();
  const filename = getFilenameFromContentDisposition(resp.headers.get('content-disposition')) || `secure-connector-windows-nssm-${connectorId}.tgz`;

  const url = window.URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    window.URL.revokeObjectURL(url);
  }
}
